/**
 * Acoperirea vitrinelor pentru adresa din sesiune, disponibila oriunde in arbore.
 *
 * O singura cerere (`POST /api/storefronts/acoperire`) la fiecare schimbare de
 * adresa, nu una per card si nu una per pagina: pagina de intrare, cardurile din
 * blocuri si pastila din antet citesc acelasi raspuns.
 *
 * Trei stari pe care consumatorii trebuie sa le deosebeasca:
 *  - `unknown` — fara adresa, sau core-ul nu are ruta (mai vechi, ADR-002), sau
 *    cererea a picat: cardurile NU spun nimic despre acoperire;
 *  - `loading` — se verifica; cardurile pot arata un schelet in randul de acoperire;
 *  - `ready` — se stie per vitrina: livrabil / in afara zonei / fara restaurant.
 *
 * „Nu stiu" nu se afiseaza niciodata ca „nu livram": un card gri pe o vitrina
 * care de fapt livreaza pierde comanda fara ca nimeni sa afle.
 */

import { createContext, useContext, useMemo, type ReactNode } from "react";

import { CORE_ERROR_CODES, type StorefrontCoverageEntryDto } from "@deliveryapp/shared";

import { fetchStorefrontCoverage } from "../api/client";
import { useBootstrap } from "./BootstrapProvider";
import { useCart } from "./CartProvider";
import { useLoadable } from "./useLoadable";

export interface CoverageContextValue {
  state: "unknown" | "loading" | "ready";
  /** `null` cand nu se stie (fara adresa, ruta lipsa, eroare). */
  entryFor: (slug: string) => StorefrontCoverageEntryDto | null;
  /** Cate vitrine livreaza la adresa; `null` cand nu se stie. */
  deliverableCount: number | null;
  truncated: boolean;
  reload: () => void;
}

const CoverageContext = createContext<CoverageContextValue | null>(null);

export function CoverageProvider({ children }: { children: ReactNode }) {
  const { marketKey } = useBootstrap();
  const { session } = useCart();
  const address = session.address;
  const lat = address?.lat ?? null;
  const lng = address?.lng ?? null;

  const coverage = useLoadable(
    (signal) =>
      lat === null || lng === null
        ? Promise.resolve(null)
        : fetchStorefrontCoverage(marketKey, { lat, lng }, signal),
    [marketKey, lat, lng],
  );

  const value = useMemo<CoverageContextValue>(() => {
    // Ruta lipsa pe un core mai vechi = „nu stiu", nu eroare de aratat.
    const unsupported = coverage.error !== null && coverage.error.code === CORE_ERROR_CODES.ROUTE_NOT_FOUND;
    const data = coverage.data;
    const bySlug = new Map<string, StorefrontCoverageEntryDto>();
    if (data !== null) for (const entry of data.storefronts) bySlug.set(entry.slug, entry);
    const state: CoverageContextValue["state"] =
      lat === null || unsupported || coverage.error !== null
        ? "unknown"
        : coverage.state === "loading" || data === null
          ? "loading"
          : "ready";
    return {
      state,
      entryFor: (slug) => (state === "ready" ? (bySlug.get(slug) ?? null) : null),
      deliverableCount: state === "ready" && data !== null ? data.storefronts.filter((entry) => entry.status === "deliverable").length : null,
      truncated: data?.truncated === true,
      reload: coverage.reload,
    };
  }, [coverage.data, coverage.error, coverage.state, coverage.reload, lat]);

  return <CoverageContext.Provider value={value}>{children}</CoverageContext.Provider>;
}

export function useCoverage(): CoverageContextValue {
  const value = useContext(CoverageContext);
  if (value === null) throw new Error("useCoverage a fost apelat in afara lui CoverageProvider.");
  return value;
}
